import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  ApiRef,
  createApiRef,
  gitlabAuthApiRef,
  googleAuthApiRef,
  microsoftAuthApiRef,
  oktaAuthApiRef,
  oneloginAuthApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import {
  kubernetesAuthProvidersApiRef,
  KubernetesAuthProviders,
} from '@backstage/plugin-kubernetes-react';

export const oidcAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.oidc',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'oidc',
          title: 'ESET ADFS',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        // IMPORTANT: Must match additionalScopes in app-config.yaml for consistent auth/refresh flow
        // Including offline_access ensures refresh token functionality
        // Simplified to only scopes that ADFS actually respects and grants
        // ADFS issues refresh tokens server-side without needing offline_access scope
        defaultScopes: [
          'openid',       // Required for OIDC flow
          'allatclaims',  // ADFS-specific scope that provides all claims (email, upn, groupsid, profile)
          'offline_access', // Optional for refresh token functionality
        ],
      }),
  }),
  // Custom Kubernetes auth providers configuration to support custom OIDC provider
  createApiFactory({
    api: kubernetesAuthProvidersApiRef,
    deps: {
      gitlabAuthApi: gitlabAuthApiRef,
      googleAuthApi: googleAuthApiRef,
      microsoftAuthApi: microsoftAuthApiRef,
      oktaAuthApi: oktaAuthApiRef,
      oneloginAuthApi: oneloginAuthApiRef,
      oidcAuthApi: oidcAuthApiRef,
    },
    factory: ({
      gitlabAuthApi,
      googleAuthApi,
      microsoftAuthApi,
      oktaAuthApi,
      oneloginAuthApi,
      oidcAuthApi,
    }) => {
      // Register all default OIDC providers plus our custom OIDC provider
      const oidcProviders = {
        gitlab: gitlabAuthApi,
        google: googleAuthApi,
        microsoft: microsoftAuthApi,
        okta: oktaAuthApi,
        onelogin: oneloginAuthApi,
        oidc: oidcAuthApi, // Custom ADFS OIDC provider
      };

      return new KubernetesAuthProviders({
        microsoftAuthApi,
        googleAuthApi,
        oidcProviders,
      });
    },
  }),
];
